import { useRef } from 'react';
import clsx from 'clsx';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import IconClose from '@/assets/icons/icon-close.svg?react';
import NodeFieldLayout from '@/components/inspector-fields/FormFieldSettings/NodeFieldLayout';
import NodeFieldStyle from '@/components/inspector-fields/FormFieldSettings/NodeFieldStyle';
import NodeFieldStrokeWidth from '@/components/inspector-fields/FormFieldSettings/NodeFieldStrokeWidth';
import NodeFieldTextSize from '@/components/inspector-fields/FormFieldSettings/NodeFieldTextSize';
import NodeFieldTextSpacing from '@/components/inspector-fields/FormFieldSettings/NodeFieldTextSpacing';
import NodeFieldLabelSize from '@/components/inspector-fields/FormFieldSettings/NodeFieldLabelSize';
import NodeFieldLabelSpacing from '@/components/inspector-fields/FormFieldSettings/NodeFieldLabelSpacing';
import NodeShadowField from '@/components/inspector-fields/ShadowField/NodeShadowField';
import NodeFieldInputFontFamily from '@/components/inspector-fields/FormFieldSettings/NodeFieldInputFontFamily';
import NodeFieldInputFontWeight from '@/components/inspector-fields/FormFieldSettings/NodeFieldInputFontWeight';
import NodeFieldLabelFontFamily from '@/components/inspector-fields/FormFieldSettings/NodeFieldLabelFontFamily';
import NodeFieldLabelFontWeight from '@/components/inspector-fields/FormFieldSettings/NodeFieldLabelFontWeight';
import NodeColorField from '@/components/inspector-fields/ColorField/NodeColorField';

interface FormStylesFieldProps {
  className?: string;
  onClose?: () => void;
  outside?: boolean;
  floatingPosition?: { top: number; left: number };
  formId?: string;
}

// Inner component that uses useEditor to select the form node
const FormStylesFieldInner: React.FC<FormStylesFieldProps> = ({
  className = '',
  onClose,
  outside = false,
  floatingPosition,
  formId,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Select the form node to make it active for our sub-components
  // Commented out because it causes focus issues with dropdown menus
  // useEffect(() => {
  //   if (formId) {
  //     actions.selectNode(formId);
  //   }
  // }, [formId, actions]);

  if (!formId) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        'inspector bg-neutral-0 flex h-[560px] max-h-[560px] w-[248px] flex-col overflow-hidden rounded-lg shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-neutral-100',
        outside ? 'fixed' : 'relative',
        className,
      )}
      style={
        outside && floatingPosition
          ? { top: floatingPosition.top, left: floatingPosition.left - 8 }
          : undefined
      }
    >
      {/* Header */}
      <div className="bg-neutral-0 inline-flex h-10 min-h-10 w-full items-center justify-between rounded-t-lg border border-neutral-100 px-3">
        <div className="text-xs leading-5 font-semibold text-neutral-900">Form Style</div>
        <IconSimpleButton aria-label="Close" icon={IconClose} onClick={onClose} />
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col border border-t-0 border-neutral-100">
        <div className="no-scrollbar h-[440px] flex-1 overflow-y-auto">
          <div className="flex flex-col gap-3 px-3 pt-3 pb-6">
            {/* Field Layout */}
            <NodeFieldLayout />
            <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
            {/* Field Appearance */}
            <div className="flex flex-col gap-3">
              <div className="text-xs font-semibold text-neutral-900">Field Appearance</div>
              <NodeFieldStyle />

              <NodeColorField propertyName="fieldInputBackgroundColor" label="Fill Color" />

              <NodeColorField propertyName="fieldInputHoverBackgroundColor" label="Fill Hover" />

              <NodeColorField
                propertyName="fieldInputBorderColor"
                ensureStrokeVisibleProp="fieldInputBorderWidth"
                label="Stroke Color"
              />

              <NodeColorField
                propertyName="fieldInputHoverBorderColor"
                ensureStrokeVisibleProp="fieldInputBorderWidth"
                label="Stroke Hover"
              />

              <NodeFieldStrokeWidth />

              <NodeColorField propertyName="fieldCheckboxCheckedColor" label="Option Fill" />

              <NodeShadowField
                shadowTypeProp="fieldShadowType"
                shadowSettingsProp="fieldShadowSettings"
              />
            </div>
            <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
            {/* Field Text */}
            <div className="flex flex-col gap-3">
              <div className="text-xs font-semibold text-neutral-900">Field Text</div>
              <NodeFieldInputFontFamily className="w-full" />
              <NodeFieldInputFontWeight className="w-full" />

              <NodeColorField propertyName="fieldInputTextColor" label="Text Color" />

              <NodeColorField propertyName="fieldInputHoverTextColor" label="Text Hover" />

              <NodeFieldTextSize />
              <NodeFieldTextSpacing />
            </div>
            <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
            {/* Label Text */}
            <div className="flex flex-col gap-3">
              <div className="text-xs font-semibold text-neutral-900">Label Text</div>
              <NodeFieldLabelFontFamily className="w-full" />
              <NodeFieldLabelFontWeight className="w-full" />

              <NodeColorField propertyName="fieldLabelColor" label="Text Color" />

              <NodeFieldLabelSize />
              <NodeFieldLabelSpacing />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const FormStylesField = FormStylesFieldInner;

export default FormStylesField;
